import { Dayjs } from 'dayjs'

import { SupportedPageKey, usePageDrawerActions } from '~/hooks/usePageDrawer'
import { TabValue } from '~/store/slices/selectionDrawerSlice'
import { useStore } from '~/store/store'
import { Location } from '~/storm'

/**
 * Opens and closes the selection drawer without subscribing to its state, so callers that
 * only trigger it (e.g. per-cell click handlers) don't re-render when the drawer or the
 * page drawer changes.
 */
export const useSelectionDrawerActions = () => {
    const { openPageDrawer, closePageDrawer } = usePageDrawerActions()
    const drawerActions = useStore(state => state.selectionDrawer.actions)

    // Read fresh at call time so we don't close over stale render values.
    const resolveClosePageDrawerOnClose = (pageWasOpen: boolean) => {
        const { closePageDrawerOnClose, isDrawerOpen } = useStore.getState().selectionDrawer
        return pageWasOpen ? (closePageDrawerOnClose ?? isDrawerOpen) : true
    }

    const handleClose = () => {
        // Capture before closeDrawer(), which resets closePageDrawerOnClose.
        const { closePageDrawerOnClose } = useStore.getState().selectionDrawer
        drawerActions.closeDrawer()
        if (closePageDrawerOnClose === true) {
            closePageDrawer()
        }
    }

    const actions = {
        ...drawerActions,
        closeDrawer: handleClose,
    }

    const openDateSelectionWithPageDrawer = (date: Dayjs, activeDrawerTab?: TabValue) => {
        const pageWasOpen = openPageDrawer()
        drawerActions.openDrawer({ type: 'scheduleDate', date }, resolveClosePageDrawerOnClose(pageWasOpen))
        if (activeDrawerTab) {
            drawerActions.setActiveDrawerTab(activeDrawerTab)
        }
    }

    const openLocationDateSelectionWithPageDrawer = (
        date: Dayjs,
        locationId: Location['id'],
        pageDrawerOptions?: { pageKey?: SupportedPageKey | undefined }
    ) => {
        const pageWasOpen = openPageDrawer({ openDrawerArgs: { date, locationId }, activeDrawerTab: 'comments', ...pageDrawerOptions })
        drawerActions.openDrawer({ type: 'locationDate', date, locationId }, resolveClosePageDrawerOnClose(pageWasOpen))
    }

    return {
        actions,
        openDateSelectionWithPageDrawer,
        openLocationDateSelectionWithPageDrawer,
    }
}

export const useSelectionDrawer = () => {
    const selectionDrawer = useStore(state => state.selectionDrawer)
    const selectionDrawerActions = useSelectionDrawerActions()

    return {
        ...selectionDrawer,
        ...selectionDrawerActions,
    }
}
